import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import AIEventGenerator from '../components/AIEventGenerator';
import OrganizerPicker from '../components/OrganizerPicker';
import { 
  Plus, Edit, Trash2, Calendar, MapPin, Clock, Users, 
  Ticket, Star, Eye, EyeOff, Loader2, X, Save, Image, BarChart3, Megaphone, Sparkles, QrCode, Wrench, ExternalLink, ScrollText, Copy, Coins, DollarSign
} from 'lucide-react';
import EventAnalytics from './EventAnalytics';
import ProductionManager from '../components/ProductionManager';
import TicketFeesPanel from '../components/TicketFeesPanel';
import OccurrencesEditor from '../components/OccurrencesEditor';
import ExperienceSlotManager from '../components/ExperienceSlotManager';
import EmbedCodeGenerator from '../components/EmbedCodeGenerator';
import VenueComboBox from '../components/VenueComboBox';
import EventMediaUploader from '../components/EventMediaUploader';
import OrganizerEditor from '../components/OrganizerEditor';
import {
  EventTypePicker,
  CancellationPolicyPicker,
  ExperienceFields,
  ReservationFields,
} from '../components/EventBookingFields';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each category auto-maps to a specific schema.org Event subtype in
// /app/frontend/src/utils/structuredData.js → CATEGORY_TO_EVENT_TYPE.
// Keep the keys in sync when adding new ones.
// Industry-standard list (matches Eventbrite / DICE / Resy / Tock buckets).
// Labels use "·" (interpunct) so a single category covers either name.
const CATEGORIES = [
  { value: 'concert',         label: 'Concert' },
  { value: 'live_music',      label: 'Live Music' },
  { value: 'nightlife',       label: 'Nightlife' },
  { value: 'club',            label: 'Club' },
  { value: 'party',           label: 'Party' },
  { value: 'festival',        label: 'Festival' },
  { value: 'halloween',       label: 'Halloween' },
  { value: 'nye',             label: "New Year's Eve" },
  { value: 'comedy',          label: 'Comedy' },
  { value: 'theater',         label: 'Theatre' },
  { value: 'performing_arts', label: 'Performing Arts' },
  { value: 'dance',           label: 'Dance' },
  { value: 'date_night',      label: 'Date Night' },
  { value: 'brunch',          label: 'Brunch' },
  { value: 'food',            label: 'Food' },
  { value: 'tasting',         label: 'Tasting' },
  { value: 'wine',            label: 'Wine Events' },
  { value: 'workshop',        label: 'Workshop' },
  { value: 'class',           label: 'Class' },
  { value: 'wellness',        label: 'Wellness' },
  { value: 'fitness',         label: 'Fitness' },
  { value: 'tour',            label: 'Tour' },
  { value: 'sports',          label: 'Sports' },
  { value: 'competition',     label: 'Competition' },
  { value: 'gala',            label: 'Gala' },
  { value: 'fundraiser',      label: 'Fundraiser' },
  { value: 'charity',         label: 'Charity' },
  { value: 'networking',      label: 'Networking' },
  { value: 'conference',      label: 'Conference' },
  { value: 'family',          label: 'Family · Kids' },
  { value: 'exhibition',      label: 'Exhibition' },
  { value: 'popup',           label: 'Pop-up' },
  { value: 'art',             label: 'Art · Gallery' },
  { value: 'film',            label: 'Film · Screening' },
  { value: 'literary',        label: 'Literary · Book' },
  { value: 'holiday',         label: 'Holiday · Seasonal (Xmas, Valentine\u2019s…)' },
  { value: 'other',           label: 'Other' },
];

// Max-2 category picker: primary + optional secondary. Events store
// `categories` (array) with `category` kept in sync as categories[0].
function CategoryPicker({ categories, category, onChange, testIdPrefix }) {
  const cats = (Array.isArray(categories) && categories.length)
    ? categories
    : (category ? [category] : ['party']);
  const set = (idx, val) => {
    const next = [...cats];
    if (idx === 1 && !val) next.splice(1, 1);
    else next[idx] = val;
    onChange([...new Set(next.filter(Boolean))].slice(0, 2));
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        className="w-full h-10 px-3 border rounded-md bg-background"
        value={cats[0] || 'party'}
        onChange={(e) => set(0, e.target.value)}
        data-testid={`${testIdPrefix}-primary`}
      >
        {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <select
        className="w-full h-10 px-3 border rounded-md bg-background"
        value={cats[1] || ''}
        onChange={(e) => set(1, e.target.value)}
        data-testid={`${testIdPrefix}-secondary`}
      >
        <option value="">— No second category —</option>
        {CATEGORIES.filter((c) => c.value !== cats[0]).map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
    </div>
  );
}

const EMPTY_EVENT = {
  name: '',
  organization_id: null,
  slug: '',
  tagline: '',
  description: '',
  short_description: '',
  event_date: '',
  event_time: '',
  end_time: '',
  doors_open: '',
  city: 'denver',
  venue_name: '',
  venue_address: '',
  venue_city: 'Denver',
  venue_state: 'CO',
  venue_zip: '',
  tags: [],
  category: 'party',
  image_url: '',
  gallery_images: [],
  ticket_type: 'internal',
  ticket_url: '',
  external_price_label: '',
  ticket_embed: '',
  ticket_price_min: '',
  ticket_price_max: '',
  age_restriction: '21+',
  dress_code: '',
  highlights: [],
  is_featured: false,
  // Default to UNPUBLISHED so new events land as drafts. The organizer
  // reviews the preview / Page Builder, then clicks "Publish" from the
  // EventDashboard when they're ready to go live.
  is_published: false,
  occurrences: [],
  // ---- Experiences & Reservations defaults ----
  // `kind` defaults to "event" so the existing ticketed flow is identical
  // for legacy users. Switching kind reveals additional fields.
  kind: 'event',
  duration_minutes: '',
  lead_time_hours: 0,
  cancellation_policy: {
    type: 'flexible',
    label: 'Flexible',
    description: 'Full refund up to 24 hours before start. No refund inside 24h.',
    refund_tiers: [
      { hours_before: 24, percentage: 100 },
      { hours_before: 0, percentage: 0 },
    ],
  },
  // Experience-only fields (ignored for kind=event/reservation)
  meeting_point: '',
  what_to_bring: [],
  min_age: '',
  max_group_size: '',
  languages: [],
  itinerary: [],
  // Reservation-only fields (ignored for kind=event/experience)
  party_size_min: 1,
  party_size_max: 8,
  large_party_threshold: '',
  deposit_amount_cents: 0,
  turn_time_minutes: 90,
  seating_areas: [],
  status: 'upcoming'
};

export default function AdminEventManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const confirm = useConfirm();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Change-notification gate state. When the server rejects an edit
  // because date/time/description changed without a reason, we open
  // this dialog to capture the organizer's note and resubmit the same
  // formData with `change_reason` attached.
  const [reasonPrompt, setReasonPrompt] = useState(null); // { fields, retry: (reason)=>Promise }
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_EVENT);
  const [tagInput, setTagInput] = useState('');
  const [highlightInput, setHighlightInput] = useState('');
  
  // Internal ticketing state
  const [ticketTypes, setTicketTypes] = useState([]);
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [ttForm, setTtForm] = useState({ name: '', description: '', price: 0, quantity_total: 100, max_per_order: 10, tier: 'general', perks: [], occurrence_id: '' });
  const [ttPerkInput, setTtPerkInput] = useState('');
  const [ticketSales, setTicketSales] = useState(null);
  const [analyticsEvent, setAnalyticsEvent] = useState(null);
  const [productionEvent, setProductionEvent] = useState(null);
  const [focusTaskId, setFocusTaskId] = useState(null);

  // Deep-link: dashboard "Tasks across all events" sends users here with
  // ?event=<id>&task=<task_id> — open Production Manager for that event and
  // scroll/highlight the task.
  useEffect(() => {
    if (events.length === 0) return;
    const params = new URLSearchParams(location.search);
    const eventId = params.get('event');
    const taskId = params.get('task');
    if (eventId) {
      const ev = events.find(e => e.event_id === eventId);
      if (ev) {
        setProductionEvent(ev);
        setFocusTaskId(taskId || null);
      }
    }
  }, [events, location.search]);

  useEffect(() => {
    if (!['admin', 'organization'].includes(user?.role)) {
      navigate('/');
      return;
    }
    fetchEvents();
  }, [user]);

  // Query-string deep-links:
  //   ?new=1        → auto-open the empty Create-Event form
  //   ?ai=1         → auto-open the AI Generator modal
  //   ?edit=<id>    → auto-open the edit form for the given event
  //   ?tab=<value>  → land on a specific tab (e.g. "booking" for
  //                   experience/reservation pricing). Paired with ?edit=.
  // Lets organizers skip the empty-state click and start typing immediately.
  const [initialTab, setInitialTab] = useState('basic');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setEditingEvent(null);
      setFormData(EMPTY_EVENT);
      setShowForm(true);
    }
    if (params.get('ai') === '1') {
      setAiOpen(true);
    }
    const editId = params.get('edit');
    if (editId && events.length) {
      const ev = events.find((e) => e.event_id === editId);
      if (ev) {
        setEditingEvent(ev);
        setFormData({ ...EMPTY_EVENT, ...ev });
        setShowForm(true);
      }
    }
    const tab = params.get('tab');
    if (tab) setInitialTab(tab);
  }, [location.search, events]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/hosted-events?status=all&limit=200&include_drafts=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.event_date) {
      toast.error('Please fill in required fields');
      return;
    }

    const isCleanCreateMode =
      !editingEvent &&
      (new URLSearchParams(location.search).get('new') === '1' ||
       new URLSearchParams(location.search).get('from') === 'dashboard');

    setSaving(true);
    try {
      if (editingEvent) {
        await putEventWithReasonGate(`${API}/hosted-events/${editingEvent.event_id}`, formData, token, setReasonPrompt, setSaving);
        toast.success('Event updated successfully');
        setShowForm(false);
        setEditingEvent(null);
        setFormData(EMPTY_EVENT);
        fetchEvents();
      } else {
        const created = await axios.post(`${API}/hosted-events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created successfully');
        setShowForm(false);
        setEditingEvent(null);
        setFormData(EMPTY_EVENT);
        // When launched from the org dashboard / "Create event" CTA, land
        // the organizer on the new event's dashboard so the URL doesn't
        // still have ?new=1 (which would re-open the modal via the
        // deep-link useEffect).
        const newId = created?.data?.event_id;
        if (isCleanCreateMode && newId) {
          navigate(`/event/${newId}/manage`, { replace: true });
        } else {
          fetchEvents();
        }
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      ...EMPTY_EVENT,
      ...event,
      tags: event.tags || [],
      highlights: event.highlights || [],
      gallery_images: event.gallery_images || []
    });
    setShowForm(true);
    // Load ticket types for this event
    fetchTicketTypes(event.event_id);
    fetchTicketSales(event.event_id);
  };

  const handleDelete = async (eventId) => {
    const ok = await confirm({
      title: 'Delete this event?',
      body: 'This permanently removes the event and all its public listings. This cannot be undone.',
      confirmText: 'Delete event',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/hosted-events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete event'); }
  };

  const handleDuplicate = async (event) => {
    const ok = await confirm({
      title: `Duplicate "${event.name}"?`,
      body: 'Creates a draft copy with all tasks, vendors, staff and budget lines. Dates, statuses, actuals, scans, venue proposals, and analytics are reset.',
      confirmText: 'Duplicate event',
    });
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/hosted-events/${event.event_id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const c = res.data?.duplicated || {};
      const summary = [
        `${c.tasks || 0} task${c.tasks === 1 ? '' : 's'}`,
        `${c.vendors || 0} vendor${c.vendors === 1 ? '' : 's'}`,
        `${c.staff || 0} staff job${c.staff === 1 ? '' : 's'}`,
        `${c.budget_lines || 0} budget line${c.budget_lines === 1 ? '' : 's'}`,
      ].join(' · ');
      toast.success(`Duplicated "${event.name}" — ${summary}`);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate event');
    }
  };

  // ===== Ticket Type Management =====
  const fetchTicketTypes = async (eventId) => {
    try {
      // Send the admin/org token so hidden tiers stay visible in the editor.
      const res = await axios.get(`${API}/events/${eventId}/ticket-types`, { headers: { Authorization: `Bearer ${token}` } });
      setTicketTypes(res.data.ticket_types || []);
    } catch { setTicketTypes([]); }
  };

  const fetchTicketSales = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/ticket-sales`, { headers: { Authorization: `Bearer ${token}` } });
      setTicketSales(res.data);
    } catch { setTicketSales(null); }
  };

  const saveTicketType = async () => {
    const eventId = editingEvent?.event_id;
    if (!eventId) return;
    const payload = { ...ttForm, occurrence_id: ttForm.occurrence_id || null };
    try {
      if (editingTicketType) {
        await axios.put(`${API}/ticket-types/${editingTicketType}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ticket type updated');
      } else {
        await axios.post(`${API}/events/${eventId}/ticket-types`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ticket type created');
      }
      setEditingTicketType(null);
      setTtForm({ name: '', description: '', price: 0, quantity_total: 100, max_per_order: 10, tier: 'general', perks: [], occurrence_id: '' });
      fetchTicketTypes(eventId);
      fetchTicketSales(eventId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deleteTicketType = async (ttId) => {
    try {
      await axios.delete(`${API}/ticket-types/${ttId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket type deleted');
      fetchTicketTypes(editingEvent?.event_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Cannot delete — tickets already sold'); }
  };

  const editTicketType = (tt) => {
    setEditingTicketType(tt.ticket_type_id);
    setTtForm({ name: tt.name, description: tt.description || '', price: tt.price, quantity_total: tt.quantity_total, max_per_order: tt.max_per_order || 10, tier: tt.tier || 'general', perks: tt.perks || [], occurrence_id: tt.occurrence_id || '' });
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim().toLowerCase()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const addHighlight = () => {
    if (highlightInput.trim()) {
      setFormData({ ...formData, highlights: [...formData.highlights, highlightInput.trim()] });
      setHighlightInput('');
    }
  };

  const removeHighlight = (idx) => {
    setFormData({ ...formData, highlights: formData.highlights.filter((_, i) => i !== idx) });
  };

  // ---- Admin quick-toggles (Publish / Feature) -----------------------
  // The Featured + Published switches were removed from the form's Details
  // tab — admins now flip them from the list card itself. One PATCH call,
  // optimistic UI, snackbar on failure.
  const togglePublish = async (event) => {
    const next = !event.is_published;
    // Optimistic update
    setEvents((prev) => prev.map((e) => e.event_id === event.event_id ? { ...e, is_published: next } : e));
    try {
      await axios.put(`${API}/hosted-events/${event.event_id}`,
        { is_published: next, _skip_change_notify: true },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(next ? 'Event published' : 'Event unpublished');
    } catch (err) {
      // Roll back on failure (most likely cause: missing cover photo)
      setEvents((prev) => prev.map((e) => e.event_id === event.event_id ? { ...e, is_published: !next } : e));
      toast.error(err.response?.data?.detail || 'Could not toggle publish');
    }
  };

  const toggleFeatured = async (event) => {
    const next = !event.is_featured;
    setEvents((prev) => prev.map((e) => e.event_id === event.event_id ? { ...e, is_featured: next } : e));
    try {
      await axios.put(`${API}/hosted-events/${event.event_id}`,
        { is_featured: next, _skip_change_notify: true },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(next ? 'Pinned as featured' : 'Removed from featured');
    } catch {
      setEvents((prev) => prev.map((e) => e.event_id === event.event_id ? { ...e, is_featured: !next } : e));
      toast.error('Could not toggle featured');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Dashboard-modal mode: when the EventDashboard's "Edit details" CTA
  // sends users here with `?edit=<id>&from=dashboard`, we hide the old
  // Eventism Events Manager page entirely and render only the edit modal
  // on top of an empty backdrop. Closing the modal navigates the user
  // back to /event/<id>/manage so they never see the legacy list page.
  // ------------------------------------------------------------------
  const _qp = new URLSearchParams(location.search);
  const fromDashboard = _qp.get('from') === 'dashboard';
  const isNewFromUrl = _qp.get('new') === '1';
  // "Clean create mode" — render only the modal on a blank backdrop with
  // no legacy "Events Manager" page behind it. Triggered by the org
  // dashboard's "Create event" CTA which sends `?new=1`.
  const isCleanCreateMode = isNewFromUrl && !fromDashboard;
  const dashboardEventId = _qp.get('edit') || _qp.get('event');
  const closeAndReturnToDashboard = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData(EMPTY_EVENT);
    if (dashboardEventId) {
      navigate(`/event/${dashboardEventId}/manage`, { replace: true });
    } else {
      navigate('/organization/dashboard', { replace: true });
    }
  };


  // When invoked from the EventDashboard, render JUST the edit modal on
  // a clean backdrop — no legacy "Eventism Events Manager" page behind it.
  if (fromDashboard) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-12" data-testid="aem-dashboard-modal-only">
        {showForm && editingEvent && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={closeAndReturnToDashboard}>
            <Card className="w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>Edit Event</CardTitle>
                  <Button variant="ghost" size="icon" onClick={closeAndReturnToDashboard} data-testid="aem-close-modal">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!formData.name || !formData.event_date) {
                  toast.error('Please fill in required fields');
                  return;
                }
                setSaving(true);
                try {
                  await putEventWithReasonGate(`${API}/hosted-events/${editingEvent.event_id}`, formData, token, setReasonPrompt, setSaving);
                  toast.success('Event updated successfully');
                  closeAndReturnToDashboard();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to save event');
                } finally {
                  setSaving(false);
                }
              }}>
                <CardContent className="space-y-4">
                  {/* Organizer profile — edit inline; changes flow to every
                      event's public Organiser tab on next load. */}
                  <OrganizerEditor defaultOpen={false} />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Event Name *</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="aem-edit-name" />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Tagline</Label>
                      <Input value={formData.tagline || ''} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Event Date *</Label>
                      <Input type="date" value={formData.event_date || ''} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input type="time" value={formData.event_time || ''} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>End Time</Label>
                      <Input type="time" value={formData.end_time || ''} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Venue Name</Label>
                      <Input value={formData.venue_name || ''} onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Venue Address</Label>
                      <Input value={formData.venue_address || ''} onChange={(e) => setFormData({ ...formData, venue_address: e.target.value })} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Short description</Label>
                      <Textarea rows={3} value={formData.short_description || ''} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Description</Label>
                      <Textarea rows={6} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Cover image URL</Label>
                      <Input value={formData.image_url || ''} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Categories (up to 2)</Label>
                      <CategoryPicker
                        categories={formData.categories}
                        category={formData.category}
                        onChange={(arr) => setFormData({ ...formData, categories: arr, category: arr[0] || 'party' })}
                        testIdPrefix="edit-category"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Public URL slug</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                          /events/{formData.city || 'city'}/
                        </span>
                        <Input
                          value={formData.slug || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            slug: e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]+/g, '-')
                              .replace(/^-+|-+$/g, ''),
                          })}
                          placeholder="my-event-name"
                          data-testid="aem-edit-slug"
                          className="font-mono"
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Lowercase letters, numbers, and hyphens only. Must be unique within the city.
                      </p>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Tags</Label>
                      <Input
                        value={formData.__tagsText ?? (formData.tags || []).join(', ')}
                        onChange={(e) => {
                          const text = e.target.value;
                          // Keep the raw text so trailing commas/spaces survive
                          // mid-typing; derive the actual tag array from it.
                          const tags = text
                            .split(',')
                            .map((t) => t.trim().toLowerCase().replace(/\s+/g, '-'))
                            .filter(Boolean);
                          setFormData({ ...formData, __tagsText: text, tags });
                        }}
                        onBlur={() => {
                          // Normalize on blur — drop the raw buffer so the
                          // chip array becomes the source of truth.
                          const { __tagsText, ...rest } = formData;
                          setFormData(rest);
                        }}
                        placeholder="jazz, wine, rooftop, date-night"
                        data-testid="aem-edit-tags"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Comma-separated. Tags drive search, category pages, and the weekly recommendation email matcher.
                      </p>
                      {(formData.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {formData.tags.map((t, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-mono">
                              #{t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Need to change tickets, schedule, or fees? Use the corresponding tabs inside the Event Dashboard.
                  </p>
                </CardContent>
                <CardFooter className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={closeAndReturnToDashboard} data-testid="aem-cancel-modal">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="aem-save-modal">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Update Event
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </div>
        )}
        {/* The change-reason gate also needs to be available in
            dashboard-modal mode — without this, the 400 fires but the
            organizer never sees the prompt to leave a note. */}
        <ChangeReasonDialog prompt={reasonPrompt} onClose={() => setReasonPrompt(null)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* When the page was opened from a dashboard CTA in "create new"
            mode (`?new=1`), hide the legacy header + event list so users
            only see the create-event modal on a clean backdrop. */}
        {!isCleanCreateMode && (
        <>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Eventism Events Manager</h1>
            <p className="text-muted-foreground">Manage your hosted events, parties, and festivals</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {user?.role === 'admin' && (
              <Button variant="outline" onClick={() => navigate('/admin/ticket-holders')} data-testid="crm-btn">
                <Users className="mr-2 h-4 w-4" /> Ticket Holder CRM
              </Button>
            )}
            <Button onClick={() => setAiOpen(true)} className="bg-gradient-to-r from-indigo-600 to-emerald-600 hover:opacity-90 text-white" data-testid="ai-generate-event-btn">
              <Sparkles className="mr-2 h-4 w-4" /> AI Generate
            </Button>
            <Button onClick={() => { setShowForm(true); setEditingEvent(null); setFormData(EMPTY_EVENT); }}>
              <Plus className="mr-2 h-4 w-4" /> New Event
            </Button>
          </div>
        </div>
        </>
        )}

        <AIEventGenerator
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          onCreated={() => fetchEvents()}
        />

        {/* Event Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <Card className="w-full max-w-4xl my-8">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</CardTitle>
                  <div className="flex items-center gap-2">
                    {editingEvent?.event_id && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const city = editingEvent.city || formData.city || 'denver';
                            const slug = editingEvent.slug || formData.slug || editingEvent.event_id;
                            window.open(`/events/${city}/${slug}`, '_blank', 'noopener,noreferrer');
                          }}
                          data-testid="header-preview-event-btn"
                          className="border-emerald-500/40 text-emerald-300 hover:text-emerald-200 hover:border-emerald-400"
                        >
                          <Eye className="h-4 w-4 mr-1.5" /> Preview event page
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/events/${editingEvent.event_id}/page-builder`)}
                          data-testid="header-page-builder-btn"
                          className="border-indigo-500/40 text-indigo-300 hover:text-indigo-200 hover:border-indigo-400"
                        >
                          <Edit className="h-4 w-4 mr-1.5" /> Open Page Builder
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => {
                      setShowForm(false);
                      setEditingEvent(null);
                      setFormData(EMPTY_EVENT);
                      if (isCleanCreateMode) {
                        navigate('/organization/dashboard', { replace: true });
                      }
                    }} data-testid="aem-close-form-btn">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-6">
                  <Tabs key={initialTab} defaultValue={initialTab}>
                    <TabsList className="w-full">
                      <TabsTrigger value="basic" className="flex-1">Basic Info</TabsTrigger>
                      <TabsTrigger value="venue" className="flex-1">Venue</TabsTrigger>
                      <TabsTrigger value="schedule" className="flex-1" data-testid="tab-schedule">Schedule</TabsTrigger>
                      {/* Pricing for experiences lives per-slot; for
                          reservations it lives as a per-guest deposit.
                          The "Booking" tab is THE pricing tab for those
                          two kinds — label it clearly so admins don't
                          look for a separate Tickets tab that doesn't
                          apply. */}
                      {(formData.kind === 'experience' || formData.kind === 'reservation') && (
                        <TabsTrigger value="booking" className="flex-1" data-testid="tab-booking">
                          {formData.kind === 'experience' ? 'Slots & Pricing' : 'Reservation & Pricing'}
                        </TabsTrigger>
                      )}
                      {(formData.kind === 'experience' || formData.kind === 'reservation') && (
                        <TabsTrigger value="embed" className="flex-1" data-testid="tab-embed">
                          Embed
                        </TabsTrigger>
                      )}
                      {/* Tickets tab applies to classic ticketed events
                          ONLY. Experiences and reservations are priced
                          through their dedicated tab above, not via
                          ticket tiers. */}
                      {formData.kind !== 'experience' && formData.kind !== 'reservation' && (
                        <TabsTrigger value="tickets" className="flex-1">Tickets</TabsTrigger>
                      )}
                      <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4 mt-4">
                      {/* Cover photo + gallery FIRST — visual identity
                          before metadata. Mirrors how creators upload on
                          Eventbrite / DICE / Tock. Server enforces a
                          cover photo before publish (returns 400). */}
                      <EventMediaUploader
                        formData={formData}
                        setFormData={setFormData}
                        token={token}
                      />
                      {/* Type picker — every event starts as "event". Switching
                          to Experience or Reservation reveals a dedicated tab
                          with kind-specific fields (duration, meeting point,
                          party size, etc.). */}
                      <EventTypePicker
                        kind={formData.kind}
                        onChange={(k) => setFormData({ ...formData, kind: k })}
                      />
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Event Name *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Denver NYE Black Tie Party"
                            required
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Tagline</Label>
                          <Input
                            value={formData.tagline}
                            onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                            placeholder="Ring in the New Year in style"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Event Date *</Label>
                          <Input
                            type="date"
                            value={formData.event_date}
                            onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Categories (up to 2)</Label>
                          <CategoryPicker
                            categories={formData.categories}
                            category={formData.category}
                            onChange={(arr) => setFormData({ ...formData, categories: arr, category: arr[0] || 'party' })}
                            testIdPrefix="event-category"
                          />
                          <p className="text-[10px] text-muted-foreground leading-snug">
                            Pick a primary and (optionally) a second category — e.g. Halloween + Party. Each maps to a schema.org subtype for SEO.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Start Time</Label>
                          <Input
                            type="time"
                            value={formData.event_time}
                            onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Time</Label>
                          <Input
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Doors Open</Label>
                          <Input
                            type="time"
                            value={formData.doors_open}
                            onChange={(e) => setFormData({ ...formData, doors_open: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value.toLowerCase() })}
                            placeholder="denver"
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Short Description</Label>
                          <Input
                            value={formData.short_description}
                            onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                            placeholder="Brief description for listings"
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Full Description</Label>
                          <Textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Detailed event description..."
                            className="min-h-[120px]"
                          />
                        </div>
                        {/* CRM profiling — these feed the auto-tagging engine so
                            Event Marketing can target by music + food + city. */}
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Music Genres (CRM tagging)</Label>
                          <p className="text-[11px] text-muted-foreground">Tag this event with the music styles attendees will hear — used to build customer profiles for targeted marketing.</p>
                          <div className="flex flex-wrap gap-1.5" data-testid="music-genres-picker">
                            {['rock', 'pop', 'hip_hop', 'edm', 'country', 'jazz', 'blues', 'rnb', 'indie', 'latin', 'reggae', 'classical', 'folk', 'metal', 'punk', 'funk', 'world'].map(g => {
                              const on = (formData.music_genres || []).includes(g);
                              const label = g.replace(/_/g, '-').replace(/\b\w/g, c => c.toUpperCase());
                              return (
                                <button
                                  key={g}
                                  type="button"
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${on ? 'bg-fuchsia-500 text-white border-fuchsia-500' : 'bg-background border-border hover:bg-muted'}`}
                                  onClick={() => {
                                    const cur = new Set(formData.music_genres || []);
                                    if (cur.has(g)) cur.delete(g); else cur.add(g);
                                    setFormData({ ...formData, music_genres: Array.from(cur) });
                                  }}
                                  data-testid={`music-genre-${g}`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!formData.food_included}
                              onChange={(e) => setFormData({ ...formData, food_included: e.target.checked })}
                              data-testid="food-included-checkbox"
                            />
                            Food included / served
                          </Label>
                        </div>
                        <div className="space-y-2">
                          <Label>Food details (optional)</Label>
                          <Input
                            value={formData.food_details || ''}
                            onChange={(e) => setFormData({ ...formData, food_details: e.target.value })}
                            placeholder="e.g. Heavy appetizers, Tacos by Chef Luis"
                            disabled={!formData.food_included}
                            data-testid="food-details-input"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="venue" className="space-y-4 mt-4">
                      {/* Verified-first venue picker: searches our existing
                          venues DB before falling back to Google Places.
                          Picking a Places result imports the venue into our
                          DB so it's verified for next time. */}
                      <VenueComboBox formData={formData} setFormData={setFormData} token={token} />
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Address</Label>
                          <Input
                            value={formData.venue_address}
                            onChange={(e) => setFormData({ ...formData, venue_address: e.target.value })}
                            placeholder="123 Main Street"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>City</Label>
                          <Input
                            value={formData.venue_city}
                            onChange={(e) => setFormData({ ...formData, venue_city: e.target.value })}
                            placeholder="Denver"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={formData.venue_state}
                            onChange={(e) => setFormData({ ...formData, venue_state: e.target.value })}
                            placeholder="CO"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>ZIP Code</Label>
                          <Input
                            value={formData.venue_zip}
                            onChange={(e) => setFormData({ ...formData, venue_zip: e.target.value })}
                            placeholder="80202"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="schedule" className="space-y-4 mt-4" data-testid="schedule-tab-content">
                      <OccurrencesEditor
                        occurrences={formData.occurrences || []}
                        onChange={(occs) => setFormData({ ...formData, occurrences: occs })}
                      />
                    </TabsContent>

                    {(formData.kind === 'experience' || formData.kind === 'reservation') && (
                      <TabsContent value="booking" className="space-y-4 mt-4" data-testid="booking-tab-content">
                        {formData.kind === 'experience' ? (
                          <>
                            {/* Header explainer — slots ARE the tickets for
                                experiences. Each slot has a date/time,
                                capacity, and price-per-guest. No separate
                                ticket tiers. */}
                            <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3">
                              <p className="text-sm font-bold text-fuchsia-300 mb-1">How experience pricing works</p>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                Each <strong>slot</strong> is one bookable session (date · time · price · capacity).
                                Guests pick a slot, pick how many people, and pay the per-guest price.
                                Add as many slots as you want — recurring tastings, weekend tours, etc.
                              </p>
                            </div>
                            <ExperienceFields formData={formData} setFormData={setFormData} />
                            {editingEvent?.event_id ? (
                              <div className="border-t pt-4">
                                <ExperienceSlotManager eventId={editingEvent?.event_id} token={token} />
                              </div>
                            ) : (
                              <div className="border-t pt-4">
                                <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                                  Save the experience first to add bookable slots (date · time · price · capacity).
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                              <p className="text-sm font-bold text-emerald-300 mb-1">How reservation pricing works</p>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                Reservations don&apos;t use ticket tiers. Set a <strong>per-guest deposit</strong>
                                that charges automatically when a booking party meets the large-party threshold
                                (e.g. parties of 6+). Smaller bookings remain free holds.
                              </p>
                            </div>
                            <ReservationFields formData={formData} setFormData={setFormData} />
                          </>
                        )}
                      </TabsContent>
                    )}

                    {(formData.kind === 'experience' || formData.kind === 'reservation') && (
                      <TabsContent value="embed" className="space-y-4 mt-4" data-testid="embed-tab-content">
                        <EmbedCodeGenerator event={editingEvent} />
                      </TabsContent>
                    )}

                    <TabsContent value="tickets" className="space-y-4 mt-4">
                      {/* Ticket Mode Selector */}
                      <div className="space-y-2">
                        <Label>Ticketing Mode</Label>
                        <select className="w-full h-10 px-3 border rounded-md bg-background" value={formData.ticket_type} onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}>
                          <option value="internal">Eventism Internal Ticketing</option>
                          <option value="external">External Link</option>
                          <option value="embed">HTML Embed</option>
                        </select>
                      </div>

                      {formData.ticket_type === 'external' && (
                        <div className="space-y-2">
                          <Label>Ticket URL</Label>
                          <Input value={formData.ticket_url} onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })} placeholder="https://eventbrite.com/..." />
                          <Label>Display price (optional — shown instead of a price badge)</Label>
                          <Input value={formData.external_price_label || ''} onChange={(e) => setFormData({ ...formData, external_price_label: e.target.value })} placeholder={'e.g. "From $45 on Ticketmaster"'} data-testid="aem-external-price-label" />
                          <p className="text-[11px] text-muted-foreground">External listings never show a "Free" badge. Leave blank to show just the Get Tickets button.</p>
                        </div>
                      )}
                      {formData.ticket_type === 'embed' && (
                        <div className="space-y-2">
                          <Label>Embed Code (HTML)</Label>
                          <Textarea value={formData.ticket_embed} onChange={(e) => setFormData({ ...formData, ticket_embed: e.target.value })} placeholder="<iframe src='...'></iframe>" className="min-h-[80px] font-mono text-sm" />
                        </div>
                      )}

                      {/* Internal Ticketing — Ticket Types */}
                      {formData.ticket_type === 'internal' && editingEvent && (
                        <div className="space-y-4">
                          {/* Sales Summary */}
                          {ticketSales && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div className="text-center p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                                <p className="text-xl font-bold">{ticketSales.total_tickets_sold}</p>
                                <p className="text-[10px] text-muted-foreground">Tickets Sold</p>
                              </div>
                              <div className="text-center p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                                <p className="text-xl font-bold text-emerald-400">${ticketSales.total_revenue.toLocaleString()}</p>
                                <p className="text-[10px] text-muted-foreground">Revenue</p>
                              </div>
                              <div className="text-center p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                                <p className="text-xl font-bold">{ticketSales.total_capacity}</p>
                                <p className="text-[10px] text-muted-foreground">Total Capacity</p>
                              </div>
                              <div className="text-center p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                                <p className="text-xl font-bold">{ticketSales.orders_count}</p>
                                <p className="text-[10px] text-muted-foreground">Orders</p>
                              </div>
                            </div>
                          )}

                          {/* Existing Ticket Types */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-semibold">Ticket Types ({ticketTypes.length})</Label>
                              <div className="flex items-center gap-1.5">
                                <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(`/admin/events/${editingEvent.event_id}/transactions`)} data-testid="open-transactions">
                                  <DollarSign className="h-3 w-3 mr-1" /> Transactions
                                </Button>
                                <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(`/admin/checkin/${editingEvent.event_id}`)} data-testid="open-checkin">
                                  <Users className="h-3 w-3 mr-1" /> Check-In Scanner
                                </Button>
                              </div>
                            </div>
                            {ticketTypes.length === 0 && (
                              <p className="text-xs text-muted-foreground py-2">No ticket types yet. Add one below.</p>
                            )}
                            {ticketTypes.map(tt => (
                              <div key={tt.ticket_type_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border" data-testid={`tt-${tt.ticket_type_id}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{tt.name}</p>
                                    <Badge variant="outline" className="text-[9px] capitalize">{tt.tier}</Badge>
                                    {!tt.is_active && <Badge className="bg-red-500/20 text-red-400 text-[9px]">Inactive</Badge>}
                                  </div>
                                  <p className="text-xs text-muted-foreground">${tt.price} &middot; {tt.quantity_sold || 0}/{tt.quantity_total} sold &middot; Max {tt.max_per_order}/order</p>
                                </div>
                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => editTicketType(tt)}><Edit className="h-3 w-3" /></Button>
                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteTicketType(tt.ticket_type_id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            ))}
                          </div>

                          {/* Add/Edit Ticket Type Form */}
                          <Card className="bg-muted/20 border-dashed">
                            <CardContent className="p-4 space-y-3">
                              <h4 className="text-sm font-semibold">{editingTicketType ? 'Edit Ticket Type' : 'Add Ticket Type'}</h4>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Name</Label>
                                  <Input value={ttForm.name} onChange={e => setTtForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. General Admission" className="h-8 text-sm" data-testid="tt-name" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Tier</Label>
                                  <select value={ttForm.tier} onChange={e => setTtForm(p => ({ ...p, tier: e.target.value }))} className="w-full h-8 px-2 border rounded-md bg-background text-sm" data-testid="tt-tier">
                                    <option value="general">General Admission</option>
                                    <option value="vip">VIP</option>
                                    <option value="early_bird">Early Bird</option>
                                    <option value="group">Group</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Price ($)</Label>
                                  <Input type="number" value={ttForm.price} onChange={e => setTtForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" data-testid="tt-price" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Total Quantity</Label>
                                  <Input type="number" value={ttForm.quantity_total} onChange={e => setTtForm(p => ({ ...p, quantity_total: parseInt(e.target.value) || 0 }))} className="h-8 text-sm" data-testid="tt-qty" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Max Per Order</Label>
                                  <Input type="number" value={ttForm.max_per_order} onChange={e => setTtForm(p => ({ ...p, max_per_order: parseInt(e.target.value) || 1 }))} className="h-8 text-sm" />
                                </div>
                                {(formData.occurrences || []).length > 0 && (
                                  <div className="col-span-2 space-y-1">
                                    <Label className="text-xs">Available on (multi-day)</Label>
                                    <select
                                      value={ttForm.occurrence_id || ''}
                                      onChange={e => setTtForm(p => ({ ...p, occurrence_id: e.target.value }))}
                                      className="w-full h-8 px-2 border rounded-md bg-background text-sm"
                                      data-testid="tt-occurrence"
                                    >
                                      <option value="">All days</option>
                                      {(formData.occurrences || []).map((occ, idx) => (
                                        <option key={occ.occurrence_id || idx} value={occ.occurrence_id}>
                                          {occ.date}{occ.label ? ` — ${occ.label}` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                <div className="col-span-2 space-y-1">
                                  <Label className="text-xs">Description</Label>
                                  <Input value={ttForm.description} onChange={e => setTtForm(p => ({ ...p, description: e.target.value }))} placeholder="What's included with this ticket..." className="h-8 text-sm" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                  <Label className="text-xs">Perks</Label>
                                  <div className="flex gap-2">
                                    <Input value={ttPerkInput} onChange={e => setTtPerkInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (ttPerkInput.trim()) { setTtForm(p => ({ ...p, perks: [...p.perks, ttPerkInput.trim()] })); setTtPerkInput(''); } } }} placeholder="e.g. Priority entry" className="h-8 text-sm flex-1" />
                                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { if (ttPerkInput.trim()) { setTtForm(p => ({ ...p, perks: [...p.perks, ttPerkInput.trim()] })); setTtPerkInput(''); } }}>Add</Button>
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {ttForm.perks.map((p, i) => (
                                      <Badge key={i} variant="outline" className="text-[10px] gap-1">
                                        {p}
                                        <button onClick={() => setTtForm(prev => ({ ...prev, perks: prev.perks.filter((_, j) => j !== i) }))} className="ml-0.5 hover:text-destructive"><X className="h-2.5 w-2.5" /></button>
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button type="button" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={saveTicketType} data-testid="save-tt">
                                  <Save className="h-3 w-3 mr-1" /> {editingTicketType ? 'Update' : 'Add Ticket Type'}
                                </Button>
                                {editingTicketType && (
                                  <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => { setEditingTicketType(null); setTtForm({ name: '', description: '', price: 0, quantity_total: 100, max_per_order: 10, tier: 'general', perks: [] }); }}>Cancel</Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Price Range (for display on event listing) */}
                      <div className="space-y-2">
                        <Label>Display Price Range</Label>
                        <div className="flex gap-2">
                          <Input type="number" value={formData.ticket_price_min} onChange={(e) => setFormData({ ...formData, ticket_price_min: e.target.value })} placeholder="Min $" />
                          <span className="self-center">-</span>
                          <Input type="number" value={formData.ticket_price_max} onChange={(e) => setFormData({ ...formData, ticket_price_max: e.target.value })} placeholder="Max $" />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="space-y-4 mt-4">
                      {/* Organizer profile — powers the public Organiser tab */}
                      <OrganizerPicker
                        organizationId={formData.organization_id}
                        onChange={(orgId) => setFormData({ ...formData, organization_id: orgId })}
                        token={token}
                      />
                      {/* Cancellation Policy — applies to every kind. The
                          backend uses the chosen tiers to compute refunds
                          at cancel-time. */}
                      <CancellationPolicyPicker
                        policy={formData.cancellation_policy}
                        onChange={(p) => setFormData({ ...formData, cancellation_policy: p })}
                      />
                      {/* Tags — live here so they're easy to find when polishing for launch */}
                      <div className="space-y-2">
                        <Label>Tags</Label>
                        <Input
                          value={formData.__tagsText ?? (formData.tags || []).join(', ')}
                          onChange={(e) => {
                            const text = e.target.value;
                            const tags = text
                              .split(',')
                              .map((t) => t.trim().toLowerCase().replace(/\s+/g, '-'))
                              .filter(Boolean);
                            setFormData({ ...formData, __tagsText: text, tags });
                          }}
                          onBlur={() => {
                            const { __tagsText, ...rest } = formData;
                            setFormData(rest);
                          }}
                          placeholder="jazz, wine, rooftop, date-night"
                          data-testid="aem-create-tags"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          Comma-separated. Tags power search, category pages, and the weekly recommendation matcher.
                        </p>
                        {(formData.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {formData.tags.map((t, i) => (
                              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-mono">
                                #{t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Page Builder shortcut */}
                      {editingEvent?.event_id ? (
                        <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4 flex items-center gap-3 flex-wrap" data-testid="pb-shortcut">
                          <div className="p-2 rounded-lg bg-indigo-500/10 flex-shrink-0">
                            <Edit className="h-4 w-4 text-indigo-500" />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-sm font-semibold">Page Builder</p>
                            <p className="text-xs text-muted-foreground">Add FAQs, photo galleries, YouTube videos, schedules, sponsors and more — drag to reorder.</p>
                          </div>
                          <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/admin/events/${editingEvent.event_id}/page-builder`)} data-testid="pb-shortcut-btn">
                            Open Page Builder →
                          </Button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3 flex-wrap" data-testid="pb-shortcut-locked">
                          <div className="p-2 rounded-lg bg-amber-500/10 flex-shrink-0">
                            <Edit className="h-4 w-4 text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-sm font-semibold">Page Builder</p>
                            <p className="text-xs text-muted-foreground">Save the event first, then come back to add FAQs, galleries, videos, schedules and sponsors.</p>
                          </div>
                        </div>
                      )}

                      {/* Custom URL / slug */}
                      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="slug-editor">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-amber-500/10 flex-shrink-0">
                            <ExternalLink className="h-4 w-4 text-amber-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold">Public URL</p>
                            <p className="text-xs text-muted-foreground">Edit the short URL slug for this event. Use lowercase letters, numbers and hyphens. Must be unique within the city.</p>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">City (URL)</Label>
                            <Input
                              value={formData.city || ''}
                              onChange={(e) => setFormData({ ...formData, city: e.target.value.trim().toLowerCase() })}
                              placeholder="denver"
                              data-testid="event-city-input"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Slug (URL)</Label>
                            <Input
                              value={formData.slug || ''}
                              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-+/, '') })}
                              onBlur={(e) => setFormData({ ...formData, slug: e.target.value.replace(/-+$/g, '') })}
                              placeholder="my-event-slug"
                              data-testid="event-slug-input"
                            />
                          </div>
                        </div>
                        {formData.city && formData.slug && (
                          <div className="text-[11px] text-muted-foreground bg-background/40 border border-border rounded-md px-3 py-2 break-all">
                            <span className="text-muted-foreground">Public URL:</span>{' '}
                            <span className="text-emerald-400 font-mono">/events/{formData.city}/{formData.slug}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Age Restriction</Label>
                          <Input
                            value={formData.age_restriction}
                            onChange={(e) => setFormData({ ...formData, age_restriction: e.target.value })}
                            placeholder="21+"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Dress Code</Label>
                          <Input
                            value={formData.dress_code}
                            onChange={(e) => setFormData({ ...formData, dress_code: e.target.value })}
                            placeholder="Black Tie, Cocktail, Casual"
                          />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Tags (for SEO)</Label>
                          <div className="flex gap-2">
                            <Input
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              placeholder="nye, party, black-tie"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            />
                            <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.tags.map(tag => (
                              <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                                {tag} <X className="ml-1 h-3 w-3" />
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label>Event Highlights</Label>
                          <div className="flex gap-2">
                            <Input
                              value={highlightInput}
                              onChange={(e) => setHighlightInput(e.target.value)}
                              placeholder="Open bar, Live DJ, Champagne toast"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                            />
                            <Button type="button" variant="outline" onClick={addHighlight}>Add</Button>
                          </div>
                          <div className="space-y-1 mt-2">
                            {formData.highlights.map((h, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                <span>• {h}</span>
                                <Button type="button" variant="ghost" size="icon" className="h-5 w-5" onClick={() => removeHighlight(idx)}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Admin controls (Publish + Featured) moved out
                            of the form — admins now toggle these via the
                            quick-action buttons on each event row in the
                            list view. Keeps the create flow user-focused. */}
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <select
                            className="w-full h-10 px-3 border rounded-md bg-background"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          >
                            <option value="upcoming">Upcoming</option>
                            <option value="ongoing">Ongoing</option>
                            <option value="past">Past</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
                <CardFooter className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  {!editingEvent && (
                    <p className="text-xs text-muted-foreground sm:max-w-md leading-snug">
                      <strong className="text-foreground">Saves as a draft.</strong> You&apos;ll land on the event dashboard
                      where you can preview, build the page, and publish when ready.
                    </p>
                  )}
                  <div className="flex gap-3 sm:ml-auto">
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    <Button type="submit" disabled={saving}>
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                      {editingEvent ? 'Update Event' : 'Save as Draft'}
                    </Button>
                  </div>
                </CardFooter>
              </form>
            </Card>
          </div>
        )}

        {/* Events List — hidden in clean-create mode so users only see
            the modal on a blank backdrop. */}
        {!isCleanCreateMode && (
        <div className="grid gap-4">
          {events.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No events yet</p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Create Your First Event
                </Button>
              </CardContent>
            </Card>
          ) : (
            events.map(event => (
              <Card key={event.event_id} className="overflow-hidden">
                <div className="flex">
                  {event.image_url && (
                    <div className="w-48 h-32 flex-shrink-0">
                      <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{event.name}</h3>
                          {event.is_featured && <Badge className="bg-amber-500">Featured</Badge>}
                          {!event.is_published && <Badge variant="outline"><EyeOff className="h-3 w-3 mr-1" />Draft</Badge>}
                          <Badge variant={event.status === 'upcoming' ? 'default' : 'secondary'}>
                            {event.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{event.event_date}</span>
                          {event.event_time && <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{event.event_time}</span>}
                          {event.venue_name && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{event.venue_name}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {event.tags?.slice(0, 5).map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Admin quick-toggles — replace the removed
                            Published/Featured switches in the form. One
                            click here flips the underlying flag via
                            PATCH /events/:id. Publish gate (cover photo
                            required) is enforced server-side, so the
                            optimistic update will roll back if the API
                            rejects it. */}
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => togglePublish(event)}
                          title={event.is_published ? 'Unpublish (move to draft)' : 'Publish event'}
                          data-testid={`toggle-publish-${event.event_id}`}
                        >
                          {event.is_published
                            ? <Eye className="h-4 w-4 text-emerald-400" />
                            : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => toggleFeatured(event)}
                          title={event.is_featured ? 'Remove featured' : 'Mark as featured'}
                          data-testid={`toggle-featured-${event.event_id}`}
                        >
                          <Star className={`h-4 w-4 ${event.is_featured ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
                        </Button>
                        <Button
                          onClick={() => navigate(`/event/${event.event_id}/manage`)}
                          data-testid={`manage-${event.event_id}`}
                          size="sm"
                          className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
                        >
                          <Wrench className="h-3.5 w-3.5 mr-1.5" /> Manage
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => window.open(`/events/${event.city}/${event.slug}`, '_blank')} title="View live page" data-testid={`view-${event.event_id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(event)} title="Edit details" data-testid={`edit-${event.event_id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDuplicate(event)} title="Duplicate event" data-testid={`duplicate-${event.event_id}`}>
                          <Copy className="h-4 w-4 text-cyan-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(event.event_id)} title="Delete" data-testid={`delete-${event.event_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
        )}
        {/* Analytics Modal */}
        {analyticsEvent && (
          <EventAnalytics
            eventId={analyticsEvent.event_id}
            eventName={analyticsEvent.name}
            onClose={() => setAnalyticsEvent(null)}
          />
        )}
        {/* Production Manager Modal */}
        {productionEvent && (
          <ProductionManager
            eventId={productionEvent.event_id}
            eventName={productionEvent.name}
            focusTaskId={focusTaskId}
            onClose={() => { setProductionEvent(null); setFocusTaskId(null); }}
          />
        )}

        <ChangeReasonDialog prompt={reasonPrompt} onClose={() => setReasonPrompt(null)} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers — keep at module scope so other surfaces can reuse if needed.
// ----------------------------------------------------------------------------

/**
 * PUT a hosted-event with the change-reason gate. On 400
 * `change_reason_required` we open the `reason` dialog via the
 * supplied setter, await the organizer's note, then re-PUT with
 * `change_reason` attached. Any other error bubbles up unchanged.
 *
 * `setSaving` (optional) is toggled OFF while the modal awaits input
 * so the parent's submit button stops showing its spinner — otherwise
 * the user thinks the page is frozen and never notices the modal.
 */
async function putEventWithReasonGate(url, formData, token, setReasonPrompt, setSaving) {
  const headers = { Authorization: `Bearer ${token}` };
  try {
    return await axios.put(url, formData, { headers });
  } catch (err) {
    const d = err?.response?.data?.detail;
    const isReasonGate = err?.response?.status === 400 &&
      d && typeof d === 'object' && d.error === 'change_reason_required';
    if (!isReasonGate) throw err;

    // Hand off to the user: drop the spinner so the modal is the only
    // active surface in the UI. We also fire a toast on top of the
    // modal so users who miss the popup (small screen, scrolled away,
    // etc.) still get a clear cue that we're waiting on their input.
    if (setSaving) setSaving(false);
    toast.info('Please tell attendees what changed — note required below.');
    const reason = await new Promise((resolve) => {
      setReasonPrompt({
        fields: d.fields || [],
        resolve: (r) => { setReasonPrompt(null); resolve(r); },
      });
    });
    if (!reason) {
      const cancelErr = new Error('change_reason_cancelled');
      cancelErr.response = { data: { detail: 'Edit cancelled — attendees were not notified.' } };
      throw cancelErr;
    }
    if (setSaving) setSaving(true);
    return await axios.put(url, { ...formData, change_reason: reason }, { headers });
  }
}

/**
 * Modal that captures the organizer's "reason for change" note. Required
 * field; minimum 5 characters before submit is enabled. Rendered once
 * at the bottom of AdminEventManager.
 */
function ChangeReasonDialog({ prompt, onClose }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (prompt) setReason('');
  }, [prompt]);
  const open = !!prompt;
  const canSubmit = reason.trim().length >= 5;
  const fieldLabel = (f) => ({
    event_date: 'event date', event_time: 'start time', end_time: 'end time',
    description: 'description', venue_name: 'venue', venue_address: 'venue address',
  })[f] || f;
  const changedList = (prompt?.fields || []).map(fieldLabel).join(', ');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { prompt?.resolve(null); onClose?.(); } }}>
      <DialogContent className="max-w-lg" data-testid="change-reason-modal">
        <DialogHeader>
          <DialogTitle>Tell attendees what changed</DialogTitle>
          <DialogDescription>
            You&rsquo;re changing the <strong className="text-foreground">{changedList || 'event details'}</strong>. Anyone who already bought a ticket will receive an email from your organizer name with this note quoted verbatim.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="We've moved the event back by one hour to give the venue extra setup time. Doors at 8pm sharp…"
            data-testid="change-reason-input"
          />
          <p className="text-[11px] text-muted-foreground">{reason.trim().length} characters · minimum 5</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => prompt?.resolve(null)} data-testid="change-reason-cancel">
            Cancel edit
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => prompt?.resolve(reason.trim())}
            data-testid="change-reason-submit"
            className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
          >
            Save &amp; notify attendees
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
